import { BaseElement, FormAssociated, css, html, define, upgradeProperties } from '../../../core/index.js';

const TAG_NAME = 'omni-button';

const styles = css`
:host {
  display: inline-flex;
  color: var(--white, #FFFFFF);
  font-weight: bold;
  position: relative;
  background-color: var(--primary-color, #00C0FB);
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  padding: 0 10px;
  gap: 5px;
  cursor: pointer;
  vertical-align: middle;
  user-select: none;
  height: 36px;
  font-size: 20px;
  margin: 5px;
}

button {
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: inherit;
  color: inherit;
  cursor: inherit;
}

  :host(:hover) {
    background-color: var(--highlight-color, orange);
  }

  :host([disabled]),
  :host([disabled]:hover) {
    background-color: var(--neutral, #AAAAAAFF);
  }

  :host([disabled]),
  :host([disabled]) button {
    cursor: not-allowed;
  }

  :host([size="wide"]) {
    width: 100%;
  }

  :host([size="filter"]) {
    width: 100%;
    height: 40px;
    padding: 0 16px;
  }

  :host([size="small"]) {
    font-size: 14px;
    height: 32px;
    padding: 0 10px;
  }

  :host([size="large"]) {
    font-size: 32px;
    height: 64px;
  }

  :host([state="loading"]),
  :host([state="success"]),
  :host([state="failed"]),
  :host([disabled]) {
    cursor: not-allowed;
  }

  :host([state="loading"]):hover,
  :host([state="success"]):hover,
  :host([state="failed"]):hover {
    background-color: var(--primary-color, #00C0FB);
  }

  :host([state="loading"])::before,
  :host([state="success"])::before,
  :host([state="failed"])::before {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--primary-color, #00C0FB);
    z-index: 10;
  }

  :host([state="loading"])::after,
  :host([state="success"])::after,
  :host([state="failed"])::after {
    content: '';
    display: inline-block;
    position: absolute;
    box-sizing: border-box;
    z-index: 12;
    left: 50%;
  }

  :host([state="loading"])::after {
    border: 0.25em solid var(--white, #fff);
    border-top-color: #ffffff00;
    border-radius: 50%;
    width: 1.25em;
    height: 1.25em;
    animation: spin 0.8s linear infinite;
    margin-left: -0.625em;
  }

  :host([state="success"])::after {
    font-family: 'iAmIcon' !important;
    content: '\\f00c';
    width: 1em;
    text-align: center;
    margin-left: -0.5em;
    font-size: 1.5em;
  }

  :host([state="failed"])::after {
    font-family: 'iAmIcon' !important;
    content: '\\f00d';
    width: 1em;
    text-align: center;
    margin-left: -0.5em;
    font-size: 1.5em;
  }

  :host([state="toggled"]) {
    background-color: var(--highlight-color, orange);
  }

  :host([state="toggled"]) .icon::before {
    content: '\\f00d';
  }

  .icon {
    display: none;
  }

  .icon::before {
    font-family: 'iAmIcon' !important;
  }

  :host([icon]) .icon {
    display: inline-flex;
  }

  .label:empty {
    display: none;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const template = html`
  <button part="button" type="button">
    <span class="icon" part="icon" aria-hidden="true"></span>
    <span class="label" part="label"><slot></slot></span>
  </button>
`;

class OmniButton extends FormAssociated(BaseElement) {
  static shadowStyleSheets = [styles];
  static formAssociated = super.formAssociated;
  static get observedAttributes() {
    return ['disabled', 'state', 'icon', 'type'];
  }

  constructor() {
    super();
    upgradeProperties(this, ['disabled', 'state', 'icon', 'type']);
    this._originalState = null;
    this._stateTimer = null;
    this._root.append(template.cloneNode(true));
    this._button = this._root.querySelector('button');
    this._iconEl = this._root.querySelector('.icon');
    this._button.addEventListener('click', (ev) => this._handleClick(ev));
  }

  connectedCallback() {
    super.connectedCallback?.();
    if (this._originalState === null) {
      this._originalState = this.getAttribute('state');
    }
    this._syncDisabled();
    this._syncType();
    this._syncIcon();
  }

  disconnectedCallback() {
    if (this._stateTimer) {
      clearTimeout(this._stateTimer);
      this._stateTimer = null;
    }
  }

  attributeChangedCallback(name) {
    super.attributeChangedCallback?.(name);
    switch (name) {
      case 'disabled':
        this._syncDisabled();
        break;
      case 'icon':
        this._syncIcon();
        break;
      case 'type':
        this._syncType();
        break;
      case 'state':
        this._syncState();
        break;
      default:
        break;
    }
  }

  get type() {
    return this.getAttribute('type') ?? 'button';
  }

  set type(val) {
    if (!val) this.removeAttribute('type');
    else this.setAttribute('type', val);
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(v) {
    v ? this.setAttribute('disabled', '') : this.removeAttribute('disabled');
  }

  done() {
    this._restoreOriginalState();
  }

  success() {
    this._setTemporaryState('success', 2000);
  }

  failed() {
    this._setTemporaryState('failed', 2500);
  }

  onClick() {
    if (this.hasAttribute('disableloader')) return;
    if (!this.hasAttribute('state')) {
      this.setAttribute('state', 'loading');
    }
  }

  _handleClick(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    if (this.hasAttribute('state') && this.getAttribute('state') !== 'toggled') {
      event.preventDefault();
      return;
    }

    if (this.type === 'submit') {
      event.preventDefault();
      this._submitForm();
    } else {
      this.onClick(event);
    }
  }

  _submitForm() {
    const form = this._internals?.form || (this.hasAttribute('form') ? document.getElementById(this.getAttribute('form')) : this.closest('form'));
    if (!form) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      const tmp = document.createElement('button');
      tmp.type = 'submit';
      tmp.hidden = true;
      form.appendChild(tmp);
      tmp.click();
      tmp.remove();
    }
  }

  _syncDisabled() {
    const disabled = this.disabled;
    this._button.tabIndex = disabled ? -1 : 0;
    this._button.setAttribute('aria-disabled', String(disabled));
  }

  _syncType() {
    const type = this.type;
    this._button.type = ['button', 'submit', 'reset'].includes(type) ? type : 'button';
  }

  _syncIcon() {
    const icon = this.getAttribute('icon');
    if (!icon) {
      this._iconEl.hidden = true;
      this._iconEl.className = 'icon';
      return;
    }
    this._iconEl.hidden = false;
    this._iconEl.className = `icon iAmIcon-${icon}`;
  }

  _syncState() {
    const state = this.getAttribute('state');
    if (state == null && this._stateTimer) {
      clearTimeout(this._stateTimer);
      this._stateTimer = null;
    }
  }

  _restoreOriginalState() {
    if (this._stateTimer) {
      clearTimeout(this._stateTimer);
      this._stateTimer = null;
    }
    if (this._originalState == null) {
      this.removeAttribute('state');
    } else {
      this.setAttribute('state', this._originalState);
    }
  }

  _setTemporaryState(state, duration) {
    if (this._stateTimer) {
      clearTimeout(this._stateTimer);
      this._stateTimer = null;
    }
    this.setAttribute('state', state);
    this._stateTimer = setTimeout(() => {
      this._stateTimer = null;
      this._restoreOriginalState();
    }, duration);
  }
}

define(TAG_NAME, OmniButton);

export { OmniButton };
